// src/encounter/structured-note/phrase-fill-model.ts
//
// PROD-2824 · TS-5 · the pure half of phrase-fill.tsx: a piece phrase's fill DRAFT for one note line.
// `phraseInsertOps` (the A&P note's engine) makes fresh occurrences; these read, answer and render
// them. Nothing here changes `note-pieces.ts` (its chart queries and chart text belong to another lane).
import type { TriggerAnchor } from './note-phrases';
import type { DeltaOp } from '../lib/note-delta';
import {
  PIECE_EMBED,
  embeddedPiece,
  exportPlainText,
  isPieceOpen,
  newPieceId,
  pieceLabel,
  type ChartQuery,
  type ChartSnapshot,
  type Piece,
} from '../lib/note-pieces';
import { sameChartContent } from '../lib/to-chart-snapshot';
import { chartLineText } from '../lib/line-piece-text';
import { phraseInsertOps, type NoteMacro } from '../lib/smart-phrase';

/** A phrase being filled for one line: which line, which phrase, and its occurrences so far. */
export interface PhraseFillDraft {
  /** One per opening: a phrase opened again is a new occurrence (fresh parts, fresh focus). */
  key: string;
  linkId: string;
  macro: NoteMacro;
  ops: DeltaOp[];
  /** PROD-3148: where the phrase goes in the line, when it was called with `.` typed there (note-phrases.ts
   *  `placeAt` finds it again on Complete). Absent (the block's Macros menu): it lands at the end of the line. */
  anchor?: TriggerAnchor;
}

/** Start filling `macro` for the line `linkId`: fresh occurrences, chart pieces resolved now. */
export function startPhraseFill(linkId: string, macro: NoteMacro, resolveChart: (q: ChartQuery) => ChartSnapshot, anchor?: TriggerAnchor): PhraseFillDraft {
  return { key: newPieceId(), linkId, macro, ops: phraseInsertOps(macro.value, resolveChart).ops, ...(anchor ? { anchor } : {}) };
}

/** The draft's pieces, with the index of the op each one sits in. */
export function draftPieces(ops: readonly DeltaOp[]): Array<{ index: number; piece: Piece }> {
  const out: Array<{ index: number; piece: Piece }> = [];
  ops.forEach((op, index) => {
    const piece = embeddedPiece(op);
    if (piece) out.push({ index, piece });
  });
  return out;
}

/** `ops` with the piece at `index` replaced. */
export function withPiece(ops: readonly DeltaOp[], index: number, piece: Piece): DeltaOp[] {
  return ops.map((op, i) => (i === index ? { insert: { [PIECE_EMBED]: piece } } : op));
}

// The chart link's LINE wording (one label, one "as of", D-45 time) lives in lib/line-piece-text.ts, shared
// with the line Delta (PROD-2961, note-line-delta.ts), so the prose and the saved pieces always agree.
export { chartLineText, chartValueText, stampText } from '../lib/line-piece-text';

/** What the line receives: the phrase as prose, every piece as its display, a chart link as its values
 *  with one stamp. */
export function draftText(ops: readonly DeltaOp[]): string {
  const lineOps = ops.map((op) => {
    const piece = embeddedPiece(op);
    return piece?.t === 'chart' ? { insert: chartLineText(piece) } : op;
  });
  return exportPlainText(lineOps).replace(/\n+$/, '');
}

/**
 * Each part's name AS THE CLINICIAN SEES IT (design desk advisory, 5 Oct): the words just before it in
 * the phrase — "Visit for", "In the patient's words", "Allergies as recorded" — from the start of that
 * line or sentence, without the closing colon. A part with no words before it (the phrase opens with it,
 * or another part sits right before it) falls back to the piece's own label.
 */
export function partNames(ops: readonly DeltaOp[]): Map<number, string> {
  const names = new Map<number, string>();
  let text = ''; // the phrase's words since the last part
  ops.forEach((op, index) => {
    const piece = embeddedPiece(op);
    if (!piece) {
      if (typeof op.insert === 'string') text += op.insert;
      return;
    }
    // The words since the last line break, sentence end or list separator ("; " — the library writes a
    // system's findings as one "a: {a}; b: {b}" run), without the separators around them.
    const lead = (text.split('\n').pop() ?? '').split(/[.!?;]\s+/).pop() ?? '';
    const words = lead.replace(/^[\s:;,.–—-]+/u, '').replace(/[\s:;,.–—-]+$/u, '').trim();
    names.set(index, words || pieceLabel(piece));
    text = '';
  });
  return names;
}

/**
 * Ready to complete only when NO piece is open: every pick-list chosen, every blank written, and every
 * chart link RESOLVED — its read came back, with values or explicitly with none. A chart link still
 * loading, or whose read failed, holds Complete: its "[not completed: … — not loaded yet]" wording is a
 * placeholder, and placeholder prose never enters the note, where signing would freeze it (design desk,
 * 5 Oct). The clinician can Refresh the read, or Remove the phrase.
 */
export function draftReady(ops: readonly DeltaOp[]): boolean {
  return draftPieces(ops).every(({ piece }) => !isPieceOpen(piece));
}

/** Why Complete is held: one plain sentence per open piece, in phrase order. Empty when ready. */
export function draftHoldReasons(ops: readonly DeltaOp[]): string[] {
  const names = partNames(ops);
  return draftPieces(ops)
    .filter(({ piece }) => isPieceOpen(piece))
    .map(({ index, piece }) => {
      const name = `“${names.get(index) ?? pieceLabel(piece)}”`;
      if (piece.t === 'pick') return `Choose an option for ${name}.`;
      if (piece.t === 'blank') return `Fill in ${name}.`;
      if (piece.snapshot?.status === 'error') return `${name} could not be loaded. Select Refresh to try again, or remove the phrase.`;
      return `${name} is still loading.`;
    });
}

/**
 * Refresh: the chart link at `index` reads its QUERY again against the page's current reads (new records
 * included), as a fresh snapshot with its own "as of" time and source ids. `changed` says whether what it
 * quotes changed (the time alone does not count).
 */
export function withRefreshedChart(
  ops: readonly DeltaOp[],
  index: number,
  resolveChart: (q: ChartQuery) => ChartSnapshot,
): { ops: DeltaOp[]; changed: boolean; pulledAt?: string } {
  const piece = embeddedPiece(ops[index] ?? { insert: '' });
  if (!piece || piece.t !== 'chart') return { ops: [...ops], changed: false };
  const snapshot = resolveChart(piece.query);
  const changed = !piece.snapshot || !sameChartContent(piece.snapshot, snapshot);
  return { ops: withPiece(ops, index, { ...piece, snapshot }), changed, pulledAt: snapshot.pulledAt };
}

/**
 * Make plain text: the RESOLVED chart link at `index` becomes the words it quotes (its "as of" time
 * included) and stops being a live link — no more Refresh. An unresolved link is left as it is: its
 * placeholder wording must never become text.
 */
export function withChartAsText(ops: readonly DeltaOp[], index: number): DeltaOp[] {
  const piece = embeddedPiece(ops[index] ?? { insert: '' });
  if (!piece || piece.t !== 'chart' || isPieceOpen(piece)) return [...ops];
  return ops.map((op, i) => (i === index ? { insert: chartLineText(piece) } : op));
}


/**
 * Re-resolve every chart link that is still UNRESOLVED (its read pending, or failed) from the latest reads
 * (Codex P2, PR 3 round 2): a panel opened while the page's reads were loading quotes them once they
 * arrive. A resolved quote keeps its own pull time, so the preview does not change under the clinician.
 */
export function withFreshCharts(ops: readonly DeltaOp[], resolveChart: (q: ChartQuery) => ChartSnapshot): DeltaOp[] {
  let changed = false;
  const next = ops.map((op) => {
    const piece = embeddedPiece(op);
    if (!piece || piece.t !== 'chart') return op;
    const status = piece.snapshot?.status;
    if (status === 'ok' || status === 'empty') return op;
    const snapshot = resolveChart(piece.query);
    if (snapshot.status === status) return op;
    changed = true;
    return { insert: { [PIECE_EMBED]: { ...piece, snapshot } } };
  });
  return changed ? next : [...ops];
}
